import { useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Ban, Mail, Sparkles, StickyNote } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AdminShell, { Body, Section, Stat, StagePill } from "@/components/admin/AdminShell";
import { Card, Spinner } from "@/components/Common";
import { api, enc, errMsg } from "@/lib/api";
import { SCORE_NOTE, STAGES, label, usd, useAdminGet, when } from "@/lib/admin";

const EmailDialog = ({ open, onClose, base, name }) => {
  const [f, setF] = useState({ subject: "", body: "Hi {{first_name}},\n\n" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post(`${base}/email`, f); toast.success("Email sent"); onClose(true); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl border-[#27272A] bg-[#121214]" data-testid="cc-email-dialog">
        <DialogHeader><DialogTitle className="font-display">Email {name}</DialogTitle></DialogHeader>
        <Input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} placeholder="Subject" data-testid="cc-email-subject" />
        <Textarea rows={10} value={f.body} onChange={(e) => setF({ ...f, body: e.target.value })} data-testid="cc-email-body" />
        <p className="text-[11px] text-zinc-500">Variables like {"{{first_name}}"} and {"{{resume_score}}"} are filled in for this person. The unsubscribe link is added automatically.</p>
        <Button onClick={send} disabled={busy || !f.subject.trim() || !f.body.trim()} data-testid="cc-email-send">{busy ? <Spinner /> : "Send email"}</Button>
      </DialogContent>
    </Dialog>
  );
};

const Brief = ({ base }) => {
  const [b, setB] = useState(null);
  const [busy, setBusy] = useState(false);
  const run = async () => { setBusy(true); try { setB((await api.post(`${base}/ai-brief`)).data); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  return (
    <Card className="border-[#D4AF37]/30" data-testid="cc-ai-brief">
      <div className="flex items-center justify-between gap-3"><h3 className="flex items-center gap-2 font-display text-lg text-zinc-100"><Sparkles className="h-4 w-4 text-[#D4AF37]" />Sales intelligence</h3>
        <Button size="sm" onClick={run} disabled={busy} data-testid="cc-ai-brief-button">{busy ? <><Spinner />Reading the activity…</> : b ? "Refresh brief" : "Generate AI brief"}</Button></div>
      {b ? (
        <div className="mt-4 space-y-3">
          <p className="text-sm text-zinc-300" data-testid="cc-ai-brief-summary">{b.summary}</p>
          {b.recommendations?.map((r, i) => (
            <div key={i} className="rounded-lg border border-[#27272A] bg-[#09090B] p-3 text-sm">
              <div className="font-medium text-[#F3C649]">{r.action}</div><div className="mt-0.5 text-zinc-400">{r.why}</div>
              {r.evidence?.length > 0 && <ul className="mt-1.5 list-disc pl-4 text-xs text-zinc-500">{r.evidence.map((e) => <li key={e}>{e}</li>)}</ul>}
            </div>
          ))}
        </div>
      ) : <p className="mt-2 text-xs text-zinc-500">Built only from this person's real activity. It never guesses personal circumstances.</p>}
    </Card>
  );
};

const StageTags = ({ p, base, reload }) => {
  const [tags, setTags] = useState((p.tags || []).join(", "));
  const patch = async (body) => { try { await api.patch(base, body); toast.success("Saved"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card className="space-y-3" data-testid="cc-stage-tags">
      <div className="flex flex-wrap items-center gap-2 text-sm"><span className="text-zinc-400">Stage</span><StagePill stage={p.stage} />{p.auto_stage && p.auto_stage !== p.stage && <span className="text-xs text-zinc-500">auto: {label(p.auto_stage)}</span>}</div>
      <select value={p.auto_stage && p.stage === p.auto_stage ? "" : p.stage || ""} onChange={(e) => patch({ stage: e.target.value })} className="h-9 w-full rounded-md border border-[#27272A] bg-[#18181B] px-2 text-xs text-zinc-200" data-testid="cc-stage-select">
        <option value="">Automatic</option>{STAGES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
      </select>
      <div className="flex gap-2"><Input value={tags} onChange={(e) => setTags(e.target.value)} placeholder="tags, comma separated" className="h-9" data-testid="cc-tags-input" />
        <Button size="sm" variant="outline" onClick={() => patch({ tags: tags.split(",").map((t) => t.trim()).filter(Boolean) })} data-testid="cc-tags-save">Save</Button></div>
    </Card>
  );
};

const Notes = ({ notes = [], base, reload }) => {
  const [t, setT] = useState("");
  const add = async () => { try { await api.post(`${base}/notes`, { text: t }); setT(""); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <Card className="space-y-3" data-testid="cc-notes">
      <h3 className="flex items-center gap-2 font-display text-lg text-zinc-100"><StickyNote className="h-4 w-4 text-[#D4AF37]" />Notes</h3>
      <Textarea rows={3} value={t} onChange={(e) => setT(e.target.value)} placeholder="Add a note" data-testid="cc-note-input" />
      <Button size="sm" onClick={add} disabled={!t.trim()} data-testid="cc-note-add">Add note</Button>
      {notes.map((n, i) => <div key={i} className="border-t border-[#27272A] pt-2 text-sm text-zinc-300">{n.text}<div className="text-[10px] text-zinc-500">{n.admin || n.by} · {when(n.at)}</div></div>)}
    </Card>
  );
};

const Timeline = ({ items = [] }) => (
  <Card className="space-y-3" data-testid="cc-timeline">
    {items.length ? items.map((t, i) => (
      <div key={i} className="flex gap-3 text-sm"><span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-[#D4AF37]" /><div><div className="text-zinc-200">{t.text}</div><div className="text-[11px] text-zinc-500">{label(t.type)} · {when(t.at)}</div></div></div>
    )) : <p className="text-sm text-zinc-500">No activity yet.</p>}
  </Card>
);

export default function ProspectProfile() {
  const { key } = useParams();
  const base = `/admin/cc/prospects/${enc(key)}`;
  const q = useAdminGet(base);
  const [mail, setMail] = useState(false);
  const p = q.data?.prospect || {};
  const suppress = async () => { if (!window.confirm("Stop all marketing email to this person?")) return; try { await api.post(`${base}/suppress`); toast.success("Suppressed"); q.reload(); } catch (e) { toast.error(errMsg(e)); } };

  return (
    <AdminShell title={p.name || p.email || "Prospect"} subtitle={p.email}
      actions={q.data && <>
        <Button onClick={() => setMail(true)} disabled={p.suppressed} data-testid="cc-email-button"><Mail className="h-4 w-4" />Email</Button>
        <Button variant="outline" onClick={suppress} disabled={p.suppressed} data-testid="cc-suppress-button"><Ban className="h-4 w-4" />{p.suppressed ? "Suppressed" : "Suppress"}</Button>
      </>}>
      <Body q={q}>{(d) => (
        <div className="grid gap-6 xl:grid-cols-[1fr_360px]">
          <div>
            <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
              <Stat k="Intent" v={p.intent_score ?? "—"} tone="text-[#F3C649]" testid="cc-profile-intent" />
              <Stat k="Engagement" v={p.engagement_score ?? "—"} />
              <Stat k="Resume score" v={p.resume_score ?? "—"} />
              <Stat k="Revenue (actual)" v={usd(p.revenue)} tone="text-emerald-300" />
            </div>
            <p className="mb-6 text-xs text-zinc-500" data-testid="cc-score-note">{d.score_note || SCORE_NOTE}</p>
            {p.next_action && <Card className="mb-6 border-[#D4AF37]/40 bg-[#33290D]/30" data-testid="cc-profile-next-action"><div className="text-[11px] uppercase tracking-wider text-[#D4AF37]">Next best action</div><div className="mt-1 text-zinc-100">{p.next_action}</div>{p.next_action_reason && <div className="text-xs text-zinc-400">{p.next_action_reason}</div>}</Card>}
            <Section title="Timeline"><Timeline items={d.timeline} /></Section>
            {d.emails?.length > 0 && <Section title="Emails"><Card className="space-y-2">{d.emails.map((e, i) => <div key={i} className="flex justify-between gap-3 text-sm"><span className="text-zinc-200">{e.subject}</span><span className="shrink-0 text-xs text-zinc-500">{label(e.status)} · {when(e.at || e.sent_at)}</span></div>)}</Card></Section>}
            {d.payments?.length > 0 && <Section title="Payments"><Card className="space-y-2">{d.payments.map((x, i) => <div key={i} className="flex justify-between text-sm"><span className="text-zinc-300">{x.product || x.plan || x.description}</span><span className="text-zinc-400">{usd(x.amount)} · {when(x.at || x.created_at)}</span></div>)}</Card></Section>}
            {d.attribution && <Section title="Attribution"><Card className="grid gap-3 text-sm sm:grid-cols-3">{[["First touch", d.attribution.first_touch], ["Last touch", d.attribution.last_touch], ["Assisted", d.attribution.assisted]].map(([k, v]) => <div key={k}><div className="text-[11px] uppercase text-zinc-500">{k}</div><div className="text-zinc-200">{Array.isArray(v) ? v.map((x) => x.name || x).join(", ") || "—" : (v?.name || v || "—")}</div></div>)}</Card></Section>}
          </div>
          <div className="space-y-6">
            <Brief base={base} />
            <StageTags p={p} base={base} reload={q.reload} />
            {d.subscription && <Card data-testid="cc-subscription"><div className="text-[11px] uppercase text-zinc-500">Subscription</div><div className="mt-1 text-sm text-zinc-200">{d.subscription.tier || d.subscription.product} · {label(d.subscription.status)}</div>{p.renewal && <div className="text-xs text-zinc-500">Renews {when(p.renewal)}</div>}</Card>}
            {d.enrollments?.length > 0 && <Card><div className="mb-2 text-[11px] uppercase text-zinc-500">Automations</div>{d.enrollments.map((e, i) => <div key={i} className="text-sm text-zinc-300">{e.name || e.sequence_id} · {label(e.status)}</div>)}</Card>}
            <Notes notes={d.notes} base={base} reload={q.reload} />
          </div>
        </div>
      )}</Body>
      <EmailDialog open={mail} onClose={(sent) => { setMail(false); if (sent) q.reload(); }} base={base} name={p.name || p.email} />
    </AdminShell>
  );
}
